import { siteConfig } from "@acme/config";
import type { NavItem } from "@acme/types";
import { getServicePages } from "~/lib/constants/service-pages";
import * as m from "~/paraglide/messages.js";

/**
 * Top-level navigation, exactly as the approved design sets it: what we build,
 * how the engagement runs, the problems it addresses, the work itself, what we
 * have written, who we are, and the questions people actually ask.
 *
 * Seven items rather than five. The client's latest export promotes Projects
 * and Blog out of the footer, which is why the bar now collapses to its
 * disclosure panel at 1100px instead of 900px: seven labels stop fitting
 * sooner than five.
 */
export function getNavbarLinks(): NavItem[] {
  return [
    { href: "/services", title: m.nav_services() },
    { href: "/how-we-work", title: m.nav_how_we_work() },
    { href: "/pain-points", title: m.footer_pain_points() },
    { href: "/projects", title: m.nav_projects() },
    { href: "/blog", title: m.nav_blog() },
    { href: "/about", title: m.nav_about() },
    { href: "/faq", title: m.footer_faq() },
  ];
}

/**
 * Everything the footer should reach. It repeats the header's seven, as the
 * design does, and adds the routes the header has no room for: the dedicated
 * service pages, which only the footer links sitewide, and the four routes
 * below the FAQ.
 */
export function getFooterLinks(): NavItem[] {
  return [
    { href: "/services", title: m.nav_services() },
    ...getServicePages().map((page) => ({
      href: `/services/${page.slug}`,
      title: page.nav,
    })),
    { href: "/how-we-work", title: m.nav_how_we_work() },
    { href: "/pain-points", title: m.footer_pain_points() },
    { href: "/projects", title: m.nav_projects() },
    { href: "/blog", title: m.nav_blog() },
    { href: "/about", title: m.nav_about() },
    { href: "/faq", title: m.footer_faq() },
    { href: "/contact", title: m.footer_contact() },
    { href: "/privacy-policy", title: m.footer_privacy_policy() },
    { href: "/terms-of-service", title: m.footer_terms_of_service() },
    { href: "/legal", title: m.footer_legal() },
    { href: "/brand", title: m.brand_label() },
  ];
}

export function getCtaConfig() {
  return {
    external: true,
    // Points straight at the booking page so "book a call" is the dominant,
    // identical conversion action everywhere the CTA appears.
    href: siteConfig.calendlyUrl,
    label: m.nav_cta(),
  } as const;
}
